import { useEffect, useState } from 'react';

import type { InputFields } from '../../../components/FormBuilder/components/Form/FormBuilder';
import { useTypedSelector } from '../../../hooks/use-typedselector';

// Default messages mapping for when localized messages are not available
const DEFAULT_MESSAGES: Record<string, string> = {
  // Add your key-to-default-message mappings here
  'AOB.ServiceNow.discovery.INSTANCENAME.title': 'Instance Name',
  'AOB.ServiceNow.discovery.USERNAME.title': 'Username',
  'AOB.ServiceNow.discovery.PASSWORD.title': 'Password',
};

/**
 * Custom hook to apply localization to a list of form input fields.
 *
 * This hook listens to changes in both the input field definitions and
 * the localized messages from Redux, then maps each field's `label` to
 * a localized string if available. If no localized message is found,
 * it falls back to predefined default messages instead of showing the key.
 *
 * @param {InputFields[]} fields - The list of fields whose labels need localization
 * @param {Record<string, string>} customDefaults - Optional custom default messages to override or extend the built-in defaults
 * @returns {InputFields[]} - A new list of fields with localized labels
 */
const useLocalizedFields = (
  fields: InputFields[],
  customDefaults?: Record<string, string>
) => {
  // Fetch localized messages from Redux state
  const messages = useTypedSelector((state) => state.user.messages);

  // Local state to store the updated fields with localized labels
  const [localizedFields, setLocalizedFields] = useState<InputFields[]>([]);

  useEffect(() => {
    // Guard clause: no localization if fields are missing
    if (fields.length === 0) {
      return;
    }

    // Merge default messages with custom defaults if provided
    const defaultMessages = { ...DEFAULT_MESSAGES, ...customDefaults };

    // Replace each field's label with a localized version (if available)
    const updated = fields.map((field) => {
      if (!field.label) {
        return field;
      }

      // Priority: localized message > custom default > built-in default > original key
      const localizedLabel =
        messages[field.label] ?? defaultMessages[field.label] ?? field.label;

      return {
        ...field,
        label: localizedLabel,
      };
    });

    setLocalizedFields(updated);
  }, [fields, messages, customDefaults]);

  return localizedFields;
};

export default useLocalizedFields;
